import type { Segment } from './segment';
import { segmentIdSuffix } from './segment-id';

/**
 * URL slug strategy for pseudo-brands at /portal/compare/<key>.
 *
 * Key format: `<slug>-<id6>` where:
 *   - `slug` is a friendly form of `alias` (lowercased, ASCII-folded,
 *     non-alphanumerics → '-', length-capped). Useful for the URL bar but
 *     NOT used for lookup.
 *   - `id6` is the last 6 alphanumerics of the pseudo-brand UUID. This is
 *     what we match on, so renames don't break URLs and slug collisions
 *     between two lists with the same name don't cause ambiguity.
 *
 * Lookup is purely by `id6`. A user landing on a stale slug (e.g. after the
 * owner renamed the list) still resolves correctly.
 */

const SLUG_MAX = 40;

function slugifyAlias(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX);
}

export function segmentUrlKey(pb: Pick<Segment, 'id' | 'alias'>): string {
  const base = slugifyAlias(pb.alias || '') || 'list';
  return `${base}-${segmentIdSuffix(pb.id)}`;
}

export function parseSegmentKey(key: string): { slug: string; id6: string } | null {
  const m = key.match(/^(.*)-([a-z0-9]{6})$/);
  if (!m) return null;
  return { slug: m[1], id6: m[2] };
}
